/**
 * PortNode - Generic workflow node with typed ports
 *
 * Features:
 * - Renders input handles on left side
 * - Renders output handles on right side
 * - Port labels displayed next to handles
 * - Color-coded by category
 * - Configuration preview in node body
 *
 * Used for workflow nodes (input, output, llm, etc.)
 * Basic nodes (sticky, shape) use custom components.
 */
import { memo, useMemo } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { NodeDefinition, Port } from '@deep-agents/shared-types';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * Data structure for PortNode instances.
 * Contains reference to definition and instance configuration.
 *
 * Note: Index signature required for React Flow Node<T> type compatibility
 */
export interface PortNodeData {
  /** Reference to NodeDefinition.type */
  definitionType: string;

  /** Node configuration values */
  config: Record<string, unknown>;

  /** Inline NodeDefinition (loaded from registry) */
  definition?: NodeDefinition;

  /** Display label (can override definition.label) */
  label?: string;

  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

/** React Flow node type for PortNode */
export type PortNodeType = Node<PortNodeData, 'processor'>;

// ============================================================================
// STYLING CONSTANTS
// ============================================================================

/**
 * Category colors for visual grouping.
 * Each category gets a distinct background and border color.
 */
const CATEGORY_COLORS: Record<string, { bg: string; border: string }> = {
  flow: { bg: '#dbeafe', border: '#3b82f6' },      // Blue
  logic: { bg: '#fef3c7', border: '#f59e0b' },     // Amber
  transform: { bg: '#d1fae5', border: '#10b981' }, // Emerald
  ai: { bg: 'var(--color-accent-soft)', border: 'var(--color-accent)' }, // Accent
  integration: { bg: '#fce7f3', border: '#ec4899' }, // Pink
  custom: { bg: '#f3f4f6', border: '#6b7280' },    // Gray
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate handle position for evenly distributed ports.
 *
 * @param index - Current port index (0-based)
 * @param total - Total number of ports on this side
 * @returns Position as percentage (0-100)
 */
function getHandlePosition(index: number, total: number): number {
  if (total <= 1) return 50;
  // Distribute evenly with padding at top and bottom
  const padding = 20; // % from top and bottom
  const range = 100 - 2 * padding;
  return padding + (index / (total - 1)) * range;
}

// ============================================================================
// PORT HANDLE COMPONENT
// ============================================================================

/**
 * PortHandle - Renders a single port handle with its label.
 *
 * Input ports render on the left (target handles).
 * Output ports render on the right (source handles).
 */
function PortHandle({
  port,
  index,
  total,
  type,
}: {
  port: Port;
  index: number;
  total: number;
  type: 'source' | 'target';
}) {
  const position = type === 'source' ? Position.Right : Position.Left;
  const top = getHandlePosition(index, total);

  return (
    <div
      className="absolute flex items-center gap-1"
      style={{
        top: `${top}%`,
        [type === 'source' ? 'right' : 'left']: 0,
        transform: 'translateY(-50%)',
        flexDirection: type === 'source' ? 'row' : 'row-reverse',
      }}
    >
      {/* Port label - shows on opposite side of handle */}
      <span
        className="text-xs text-gray-600 whitespace-nowrap"
        style={{
          [type === 'source' ? 'marginRight' : 'marginLeft']: '8px',
        }}
      >
        {port.label}
        {port.required && <span className="text-red-500">*</span>}
      </span>

      {/* Handle - the actual connection point */}
      <Handle
        type={type}
        position={position}
        id={port.id}
        className="!w-3 !h-3 !bg-blue-500 !border-2 !border-white"
        style={{
          top: `${top}%`,
          position: 'absolute',
          [type === 'source' ? 'right' : 'left']: '-6px',
        }}
      />
    </div>
  );
}

// ============================================================================
// MAIN PORT NODE COMPONENT
// ============================================================================

/**
 * PortNode - Generic workflow node component.
 *
 * Renders handles dynamically based on NodeDefinition's ports array.
 * Color-coded by category for visual grouping in workflows.
 *
 * Usage:
 * 1. Pass NodeDefinition in data.definition
 * 2. Pass config values in data.config
 * 3. Optionally override label with data.label
 */
export const PortNode = memo(({ id, data, selected }: NodeProps<PortNodeType>) => {
  const { definition, config, label } = data;

  // Get category colors (fallback to 'custom' if unknown category)
  const category = definition?.category || 'custom';
  const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.custom;

  // Memoize port filtering to avoid recalculating on every render
  const inputPorts = useMemo(() =>
    definition?.inputs?.filter(p => p.direction === 'input') || [],
    [definition]
  );
  const outputPorts = useMemo(() =>
    definition?.outputs?.filter(p => p.direction === 'output') || [],
    [definition]
  );

  // Calculate minimum height to fit all ports comfortably
  const minHeight = Math.max(80, Math.max(inputPorts.length, outputPorts.length) * 30 + 40);

  return (
    <>
      {/* Node resizer - visible when selected */}
      <NodeResizer
        minWidth={140}
        minHeight={minHeight}
        isVisible={selected}
        lineClassName="border-blue-500"
        handleClassName="bg-blue-500 border-2 border-white rounded"
      />

      {/* Node body */}
      <div
        className="relative rounded-lg shadow-md overflow-hidden"
        style={{
          backgroundColor: colors.bg,
          border: `2px solid ${selected ? colors.border : 'transparent'}`,
          minWidth: 140,
          minHeight,
        }}
      >
        {/* Header - category-colored strip with label */}
        <div
          className="px-3 py-2 font-medium text-sm border-b"
          style={{ backgroundColor: colors.border, color: 'white' }}
        >
          {label || definition?.label || 'Node'}
        </div>

        {/* Body - description and config preview */}
        <div className="px-3 py-2 text-xs text-gray-600">
          {definition?.description && (
            <p className="mb-1">{definition.description}</p>
          )}
          {/* Show first 3 config values as preview */}
          {config && Object.keys(config).length > 0 && (
            <div className="mt-1 space-y-0.5">
              {Object.entries(config).slice(0, 3).map(([key, value]) => (
                <div key={key} className="truncate">
                  <span className="font-medium">{key}:</span>{' '}
                  <span>{String(value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Input port handles (left side) */}
        {inputPorts.map((port, i) => (
          <PortHandle
            key={port.id}
            port={port}
            index={i}
            total={inputPorts.length}
            type="target"
          />
        ))}

        {/* Output port handles (right side) */}
        {outputPorts.map((port, i) => (
          <PortHandle
            key={port.id}
            port={port}
            index={i}
            total={outputPorts.length}
            type="source"
          />
        ))}
      </div>
    </>
  );
});

// Display name for React DevTools
PortNode.displayName = 'PortNode';
